/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { MantineProvider, useMantineTheme } from '@mantine/core';
import TopBar from './widgets/TopBar/TopBar';
import MoreButton from './widgets/MoreButton';
import TableMenu from './widgets/TableMenu';
import { useDispatch, useSelector } from 'react-redux';
import './style.css';
import {
  updateColumnOrderByTableName,
  updateColumnSizingByTableName,
  updateColumnVisibilityByTableName,
  updateDensity,
  updateShowColumnFilters,
  updateColumnPinningByTableName,
  updateIsFullScreen,
  updateCanShowShimmer,
  resetViewedRows,
  TABLE_NAMES,
} from '../../../redux_store/reducer/reducers/tableReducer/tableReducer';
import { sendNewTableSettings } from '../../../redux_store/reducer/reducers/tableReducer/api.js';
import { compare } from 'fast-json-patch';
import {
  TABLE_DEFAULT_SETTINGS, tableBodyHoverProp,
  tableBodyProp, tableHeadCellProps,
} from './table_state_styles';
import { useForceRepaintOnVisibility } from './helpers'

let timeoutIds = {};
let _delayed = false;
let _delayedTimeoutId = null;

const Table = memo((props) => {
  const {
    columns,
    data,
    fetchNextPage,
    isError,
    isFetching,
    isLoading,
    totalRowCount,
    renderRowActionMenuItems,
    sorting,
    setSorting,
    filter,
    setFilters,
    tableKey,
    enableRowSelection,
    onRowClick,
    maxHeight,
    renderRowActions,
    rowActionsTitleSize,
    rowSelection,
    setRowSelection,
    enableEditing,
    showRefreshTableButton,
    reloadData,
    state = {},
    menuActions,
    tableMenuPopoverOptions,
    tableMenuStatus,
    tableTitle,
  } = props;

  // ------------ Fix for bug with white screen 
  useForceRepaintOnVisibility('.mantine-Table-root');

  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);
  const totalFetched = data.length;
  const tableName = TABLE_NAMES[tableKey];
  const totalDBRowCount = totalRowCount ?? 50;
  const {
    isFullScreen,
    settings,
    settings: {
      commonTable: {
        density,
        showColumnFilters,
      },
    },
    canShowShimmer,
  } = useSelector((state) => state.table);
  const rowVirtualizerInstanceRef = useRef(null);

  // -------------------------- Table settings from API --------------------------
  const { columnOrder, columnVisibility, columnSizes, columnPinning } = settings[tableKey] ?? {};
  const [_showColumnFilters, _setShowColumnFilters] = useState(showColumnFilters);
  const [_columnSizing, _setColumnSizing] = useState({ ...(columnSizes || {}) });
  const [_columnOrder, _setColumnOrder] = useState([...(columnOrder || [])]);
  const [_columnVisibility, _setColumnVisibility] = useState({ ...(columnVisibility || {}) });
  const [_density, _setDensity] = useState(density || 'md');
  const [_columnPinning, _setColumnPinning] = useState({
    ...(columnPinning ? {
      right: columnPinning.right || [],
      left: columnPinning.left || [],
    } : {
      right: [],
      left: [],
    }),
  });
  const [_isFullScreen, _setIsFullScreen] = useState(isFullScreen);
  // ------------------------------------------------------------------------------

  const [_prevSettings, _setPrevSettings] = useState(settings);
  const { isInfoPaneExpanded, currentPage } = useSelector((state) => state.infoPane);

  const [delayed, setDelayed] = useState(true);

  const setCanShowShimmer = (nevValue) => {
    dispatch(updateCanShowShimmer(nevValue));
  };
  const updateDelayed = async () => {
    await clearTimeout(_delayedTimeoutId);
    _delayedTimeoutId = await setTimeout(() => {
      if (_delayed) {
        setDelayed(true);
      }
    }, 300);
  };
  const removeDelayed = async () => {
    await clearTimeout(_delayedTimeoutId);
    setDelayed(false);
  };

  useEffect(() => {
    _delayed = isLoading || isFetching;
    if (_delayed) {
      updateDelayed();
    } else {
      removeDelayed();
      setCanShowShimmer(false);
    }
  }, [isLoading, isFetching]);

  const debounce = (callback, wait, timeoutIdKey) => {
    return async (...args) => {
      await clearTimeout(timeoutIds[timeoutIdKey]);
      timeoutIds[timeoutIdKey] = await setTimeout(() => {
        callback(...args);
      }, wait);
    };
  };

  const updateCommonTableWithDelay = debounce(() => {
    const patches = compare(settings.commonTable, {
      ...settings.commonTable,
      density: _density,
      showColumnFilters: _showColumnFilters,
    });
    dispatch(sendNewTableSettings({
      path: 'commonTable',
      patch: patches,
    }));

    dispatch(updateDensity(_density));
    dispatch(updateShowColumnFilters(_showColumnFilters));
  }, 2000, 'commonTable');

  const updateTableSettingsByNameWithDelay = debounce(() => {
    const patches = compare(settings[tableKey], {
      ...settings[tableKey],
      columnPinning: _columnPinning,
      columnSizes: _columnSizing,
      columnVisibility: _columnVisibility,
      columnOrder: _columnOrder,
    });
    dispatch(sendNewTableSettings({
      path: tableKey,
      patch: patches,
    }));

    // update redux store
    dispatch(updateColumnSizingByTableName({
      newColumnSizing: _columnSizing,
      tableKey: tableKey,
    }));
    dispatch(updateColumnPinningByTableName({
      newColumnPinning: _columnPinning,
      tableKey: tableKey,
    }));
    dispatch(updateColumnOrderByTableName({
      newOrder: _columnOrder,
      tableKey: tableKey,
    }));
    dispatch(updateColumnVisibilityByTableName({
      newVisibility: _columnVisibility,
      tableKey: tableKey,
    }));
  }, 2000, tableKey);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 700 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage((data || []).length);
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount],
  );

  const onIsFullScreenChange = useCallback((e) => {
    dispatch(updateIsFullScreen({
      newIsFullScreen: e,
    }));
    _setIsFullScreen(e);
  },
    [_isFullScreen],
  );

  useEffect(() => {
    if (rowVirtualizerInstanceRef.current) {
      //scroll to the top of the table when sorting changes
      try {
        rowVirtualizerInstanceRef.current.scrollToIndex(0);
      } catch (error) {
        console.error(error);
      }
    }
    setCanShowShimmer(true);
  }, [sorting, filter]);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const tableMenu = useMemo(
    () => tableMenuPopoverOptions
      ? <TableMenu
        tableMenuPopoverOptions={tableMenuPopoverOptions}
        tableMenuStatus={tableMenuStatus}
        isLoading={delayed || canShowShimmer}
      />
      : '',
    [tableMenuPopoverOptions, delayed, tableMenuStatus],
  );

  useEffect(() => {
    if (JSON.stringify(settings) !== JSON.stringify(_prevSettings)) {
      _setPrevSettings(settings);
    }
  }, [settings]);


  useEffect(() => {
    updateTableSettingsByNameWithDelay();
  }, [_columnPinning, _columnSizing, _columnVisibility, _columnOrder]);

  useEffect(() => {
    updateCommonTableWithDelay();
    Array.from(document.getElementsByClassName('density-sensible')).forEach(e => {
      e.style.gap = _density === 'xl' ? '16px' : _density === 'md' ? '8px' : '5px';
    });
  }, [_density, _showColumnFilters]);


  const mantineTableBodyProps = useMemo(
    () => ({
      sx: {
        ...(onRowClick ? tableBodyHoverProp : tableBodyProp),
        '& td:last-child': {
          padding: !delayed && (!!renderRowActionMenuItems || !!renderRowActions) ? '0 !important' : null,
          position: !delayed && (!!renderRowActionMenuItems || !!renderRowActions) && (!_columnPinning.left.includes('mrt-row-actions') && !_columnPinning.right.includes('mrt-row-actions')) ? 'relative' : null,

          '& .mantine-Skeleton-root': {
            marginLeft: !!renderRowActionMenuItems || !!renderRowActions ? 12 : 0,
          },
        },
      },
    }),
    [delayed, _columnPinning],
  );

  const _renderRowActions = useMemo(
    () => renderRowActions
      ? renderRowActions
      : ({ row, table }) => <MoreButton
        row={row}
        canShowSkeletons={table?.options?.state?.canShowSkeletons}
        actions={renderRowActionMenuItems}
      />,
    [renderRowActions, renderRowActionMenuItems, delayed],
  );

  const renderTopToolbar = useCallback(({ table }) => <TopBar
    reloadData={reloadData}
    rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
    setCanShowShimmer={setCanShowShimmer}
    showRefreshTableButton={showRefreshTableButton}
    showFiltersButton={!!filter}
    checked={_showColumnFilters}
    setFilters={setFilters}
    setChecked={_setShowColumnFilters}
    table={table}
    menuActions={menuActions}
    tableTitle={tableTitle}
    sorting={sorting}
    setSorting={setSorting}
    columns={columns}
  />, [filter, _showColumnFilters, menuActions, totalFetched, sorting]);

  const mantineToolbarAlertBannerProps = useMemo(
    () => isError
      ? {
        color: 'red',
        children: 'Error loading data',
      }
      : undefined,
    [isError],
  );

  const _data = useMemo(
    () => ([
      ...data,
      ...(totalDBRowCount !== totalFetched
        ? Array.from({ length: totalDBRowCount - totalFetched < 10 ? totalDBRowCount - totalFetched : 10 }, (_, i) => ({
          showShimmer: true,
        }))
        : []),
    ]),
    [data, totalDBRowCount, totalFetched],
  );

  const _columns = useMemo(
    () => (columns),
    [columns],
  );

  useEffect(() => {
    return () => {
      dispatch(resetViewedRows());
    };
  }, []);

  const mantinePaperProps = useMemo(
    () => ({
      style: {
        maxWidth: `calc(100vw - ${_isFullScreen && currentPage?.uuid ? (isInfoPaneExpanded ? 360 : 45) : 0}px)`,
        transition: '0.3s',
        border: 'none',
        boxShadow: 'none',

      },
      sx: {
        minHeight: '100%',
        height: '100%',
        maxHeight: maxHeight + 100,
      },
    }),
    [_isFullScreen, isInfoPaneExpanded, maxHeight],
  );

  const mantineTableContainerProps = useMemo(
    () => ({
      sx: {
        minHeight: '100px',
        height: maxHeight && !_isFullScreen ? maxHeight + 60 : _isFullScreen ? 'calc(100% - 70px)' : 'calc(100% - 60px)',

        '&::-webkit-scrollbar-thumb': {
          background: 'grey',
          borderRadius: 10,
        },
        '&::-webkit-scrollbar': {
          width: 0,
          maxHeight: 10,
          background: 'white',
        },
      },
      style: {
        overflowY: 'auto',
        overflowX: 'auto',
        scrollbarWidth: 'auto',
        msOverflowStyle: 'auto',
      },
      onScroll: (
        event,
      ) => fetchMoreOnBottomReached(event.target),
    }),
    [columns, data, fetchNextPage, isFetching, totalFetched, totalDBRowCount],
  );

  const renderEmptyRowsFallback = useCallback(
    () => {
      return isFetching || isLoading ? <></> :
        <div style={{
          position: 'sticky',
          left: '50%',
          right: '50%',
          fontSize: 14,
          maxWidth: 1,
          color: 'gray',
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        }}>
          <div style={{
            position: 'absolute',
            width: 'max-content',
            height: '-webkit-fill-available',
            transform: 'translate(-50%, 50%)',
          }}>
            {'No records to display'}
          </div>

        </div>
    },
    [isFetching, isLoading],
  );


  const _initSettings = useMemo(
    () => ({
      manualFiltering: !enableEditing,
      manualSorting: !enableEditing,
      enableFilters: !enableEditing,
      initialState: {
        pagination: { pageSize: 15 },
        density: 'md',
        showColumnFilters: true,
        columnPinning: {
          right: !!renderRowActionMenuItems || !!renderRowActions ? ['mrt-row-actions'] : [],
          left: !!enableRowSelection ? ['mrt-row-select'] : [],
        },
      },
      onColumnOrderChange: _setColumnOrder,
      enableRowVirtualization: false,
      enableColumnVirtualization: false,
      onColumnVisibilityChange: _setColumnVisibility,
      onColumnSizingChange: _setColumnSizing,
      onDensityChange: _setDensity,
      onColumnPinningChange: _setColumnPinning,
      onIsFullScreenChange: onIsFullScreenChange,
      enableRowSelection: enableRowSelection,
      enableEditing: enableEditing,
      enableRowActions: !!renderRowActionMenuItems || !!renderRowActions,
      mantineTableBodyRowProps: (enableRowSelection ? null : (prop) => ({
        onClick: onRowClick ? () => onRowClick(prop.row.original) : null,
        sx: onRowClick ? { cursor: 'pointer' } : {},
      })),
    }),
    [],
  );

  const table = useMantineReactTable({
    ...TABLE_DEFAULT_SETTINGS,
    ..._initSettings,
    columns: _columns,
    data: _data,
    mantineTableContainerProps: {
      ref: tableContainerRef,
      ...mantineTableContainerProps,
    },
    mantineTableHeadCellProps: tableHeadCellProps,
    displayColumnDefOptions: !!renderRowActionMenuItems || !!renderRowActions ? {
      'mrt-row-actions': {
        header: tableMenu,
        size: rowActionsTitleSize ?? 50,
      },
    } : {},
    mantinePaperProps: mantinePaperProps,
    mantineSkeletonProps: { animate: false },
    mantineToolbarAlertBannerProps: mantineToolbarAlertBannerProps,
    state: {
      isLoading: false,
      sorting: sorting || [],
      filter: filter || [],
      showAlertBanner: isError,
      showProgressBars: false,
      showLoadingOverlay: false,
      showSkeletons: false,
      canShowSkeletons: canShowShimmer,
      showGlobalFilter: false,
      tableName: tableName,
      rowSelection: rowSelection ?? {},
      showColumnFilters: _showColumnFilters,
      columnOrder: _columnOrder,
      columnVisibility: _columnVisibility,
      columnSizing: _columnSizing,
      density: _density,
      columnPinning: _columnPinning,
      isFullScreen: _isFullScreen,
      ...state,
    },
    renderRowActions: _renderRowActions,
    renderTopToolbar: renderTopToolbar,
    mantineTableBodyProps: mantineTableBodyProps,
    renderSkeleton: () => null,
    onColumnFiltersChange: setFilters,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    renderEmptyRowsFallback: renderEmptyRowsFallback,

    enableStickyHeader: true,
    rowVirtualizerInstanceRef: rowVirtualizerInstanceRef,
    enableRowVirtualization: true,
    rowVirtualizerProps: {
      overscan: 6,
      // estimateSize: index => () => 70,
    },
    enableSortingRemoval: false,
    icons: {
      IconArrowsSort: () => <div className={'sort-icon'} />,
    },
  });

  return <MantineReactTable table={table} renderSkeleton={() => null} />;
});

const queryClient = new QueryClient();

const QhsTable = memo((props) => {
  return <TableWrapper columns={props?.columns}>
    <Table {...props} />
  </TableWrapper>;
});

export default QhsTable;

const TableWrapper = memo(({ children, columns }) => {
  const { settings } = useSelector((state) => state.table);
  const globalTheme = useMantineTheme();

  if (!columns || !columns.length || !settings) {
    return <div />;
  }

  return <QueryClientProvider client={queryClient}>
    <MantineProvider
      theme={{
        ...globalTheme,
        primaryColor: 'blue',
        primaryShade: 5,
      }}
    >
      {children}
    </MantineProvider>
  </QueryClientProvider>;
});
